import { supabase } from "@/core/services/supabase-client";
import { apiFetch } from "@/core/services/api-client";
import { User, Session } from "@supabase/supabase-js";

interface Profile {
  profile_id: number;
  name: string;
  createdAt: string;
  estimatedMonthlyUsdIncome: number | null;
}

interface UpdateProfileFields {
  estimatedMonthlyUsdIncome: number | null;
}

interface SignUpData {
  email: string;
  password: string;
  name: string;
}

export const authService = {
  async signInWithGoogle(redirectTo: string) {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });
    if (error) throw error;
  },

  async getCurrentSession(): Promise<Session | null> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  onAuthStateChange(callback: (session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session);
    });
  },

  // createOrUpdateProfile (POST /profiles) replaces the legacy "get or create"
  // enrichment. The API keys the row by the token's auth_id and returns the
  // profile (`createdAt` is the actual camelCase column).
  async createOrUpdateProfile(user: User): Promise<Profile> {
    return apiFetch<Profile>("/profiles", {
      method: "POST",
      body: JSON.stringify({
        name: (user.user_metadata as { full_name?: string } | undefined)?.full_name ?? "",
        email: user.email ?? undefined,
        avatar_url: (user.user_metadata as { avatar_url?: string } | undefined)?.avatar_url ?? null,
      }),
    });
  },

  // updateProfile (PUT /profiles) updates only estimatedMonthlyUsdIncome; the
  // profile is addressed by the token, so no profileId is sent (MIG-4).
  async updateProfile(fields: UpdateProfileFields): Promise<Profile> {
    return apiFetch<Profile>("/profiles", {
      method: "PUT",
      body: JSON.stringify({ estimatedMonthlyUsdIncome: fields.estimatedMonthlyUsdIncome }),
    });
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async signUp({ email, password, name }: SignUpData) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
        },
      },
    });

    if (error) throw error;

    if (data.user) {
      await this.createOrUpdateProfile(data.user);
    }

    return data;
  },
};
